/**
 * A plan as a picture (Work → Plans): its steps in waves of work, left to right — wave 1 is everything with nothing
 * to wait for, the next wave waits only on the one before — the critical path along the top in violet, each card
 * in the state of its step, and the arrows saying what waits for what. A press on a card lights its chain and
 * opens what it waits for and what it unblocks. The layout is the workbench's (planGraphLayout.ts); the cards,
 * the counts and the detail are ported from its PlansView, less what only a desktop does (opening a step in
 * another repository, setting how careful a step is, its runs).
 */
import { CircleCheckFill, CircleExclamationFill, CirclePlay, CircleXmarkFill, Clock, Eye, File as FileIcon, Flame, NodesRight, Person, PlayFill, Target, TriangleExclamationFill, Xmark } from '@gravity-ui/icons';
import { Avatar, Button, ProgressBar } from '@heroui/react';
import { type Edge, Handle, type Node, type NodeProps, Panel, Position } from '@xyflow/react';
import { type ComponentType, type ReactNode, type SVGProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FlowBandNode, FlowCanvas, type FlowCount, FlowCounts, FlowRefit } from '../flowCanvas.tsx';
import { withNodeSizes } from '../flowMinimap.ts';
import { FLOW_HANDLE, FLOW_MUTED, FLOW_TONE, flowBand, flowEdge, type FlowEdgeLook, flowFooter } from '../flowStyle.ts';
import { FitText } from '../ui/FitText.tsx';
import { Hint } from '../ui/Hint.tsx';
import { CRITICAL_TONE, layoutPlanGraph, PLAN_CARD, planChain, planCounts, type PlanEdgeLayout, type PlanGraphLayout, type PlanGraphTask, type PlanTaskState, planWantsMinimap, planWaves, TASK_LABEL, TASK_TONE } from './planGraphLayout.ts';

/** One step of the plan, as the graph reads it: the layout's fields and what a card shows. */
export interface PlanGraphStep extends PlanGraphTask {
  title: string;
  taskId?: string;
  assignee?: string;
  role?: string;
  repository?: { rootId: string; name?: string };
  estimate?: { complexity?: number; tokens?: number };
  /** The acceptance criteria it delivers. */
  done?: string[];
  files?: string[];
  applyStatus?: string;
  concerns?: string;
  qualify?: string;
}

export interface PlanGraphProps {
  planId: string;
  steps: readonly PlanGraphStep[];
  /** The critical path, in order. */
  critical: readonly string[];
  /** Criteria by id, for the "Delivers" list. */
  criteria?: ReadonlyArray<{ id: string; title: string; passed: boolean }>;
}

type PlanNodeData = PlanGraphStep & { critical: boolean; state: PlanTaskState; selected: boolean; dimmed: boolean } & Record<string, unknown>;

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

/** One shape per state, so the colour is never the only thing that says it. */
const STATE_ICON: Record<PlanTaskState, IconType> = { done: CircleCheckFill, running: PlayFill, review: Eye, blocked: CircleExclamationFill, failed: CircleXmarkFill, ready: CirclePlay, waiting: Clock };

const STATE_HINT: Record<PlanTaskState, string> = {
  done: 'Finished',
  running: 'An agent is working on this now',
  review: 'Finished and waiting for review',
  blocked: 'Blocked: it cannot go on until someone steps in',
  failed: 'Finished as a failure',
  ready: 'Everything it waits for is done: this is next',
  waiting: 'Waiting for the tasks before it',
};

const loud = (state: PlanTaskState) => state === 'blocked' || state === 'failed';

const PLAN_NODE_SIZES = { planTask: PLAN_CARD };

function initials(name: string): string {
  const words = name.trim().split(/[\s\-_]+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words.length > 1 ? words[words.length - 1]![0] : '')).toUpperCase() || '?';
}

/** Whoever the task is with: initials in a small round chip, the name beside it. */
function Owner({ name, className }: { name?: string; className?: string }) {
  return (
    <span className={`flex min-w-0 items-center gap-1.5 ${className ?? ''}`} title={name ?? 'Nobody has this task yet'}>
      <Avatar size="sm" variant="soft" color={name ? 'accent' : 'default'} className="size-5 shrink-0">
        <Avatar.Fallback className="text-xs font-semibold">{name ? initials(name) : <Person className="size-2.5" />}</Avatar.Fallback>
      </Avatar>
      <span className="truncate">{name ?? 'unassigned'}</span>
    </span>
  );
}

/** How the apply and qualify steps went, when they did not go cleanly. */
function Verdicts({ data }: { data: Pick<PlanGraphStep, 'applyStatus' | 'concerns' | 'qualify'> }) {
  return (
    <>
      {data.applyStatus === 'done_with_concerns' ? (
        <span className="flow-pill" data-tone="amber" title={data.concerns ? `Done, with concerns: ${data.concerns}` : 'Done, with concerns'}>
          <TriangleExclamationFill /> concerns
        </span>
      ) : null}
      {data.qualify === 'gap' || data.qualify === 'drift' ? (
        <span className="flow-pill" data-tone="rose" title={data.qualify === 'gap' ? 'Qualify: something the task asked for is missing' : 'Qualify: built something different from what was specified'}>
          <TriangleExclamationFill /> {data.qualify}
        </span>
      ) : null}
    </>
  );
}

function StateLabel({ state }: { state: PlanTaskState }) {
  return (
    <span className={loud(state) ? 'flow-pill' : 'plan-card__state'} data-tone={TASK_TONE[state]} data-solid={loud(state) ? '' : undefined} data-state={state} title={STATE_HINT[state]}>
      {TASK_LABEL[state]}
    </span>
  );
}

function PlanTaskNode({ data }: NodeProps<Node<PlanNodeData>>) {
  const { state } = data;
  const Icon = STATE_ICON[state];
  // A calm card for what is finished or not started; the tone reaches the card's own edge only where something is happening, or has gone wrong.
  const toned = state === 'running' || state === 'review' || loud(state);
  return (
    <div
      className="flow-card plan-card h-full w-full"
      data-tone={toned ? TASK_TONE[state] : undefined}
      data-state={state}
      data-live={state === 'running' ? '' : undefined}
      data-wash={loud(state) ? '' : undefined}
      data-quiet={data.status === 'backlog' ? '' : undefined}
      data-selected={data.selected ? '' : undefined}
      data-dimmed={data.dimmed ? '' : undefined}
    >
      <Handle type="target" position={Position.Left} className={FLOW_HANDLE} />
      <div className="flex items-center gap-1.5">
        <span className="flow-card__well" data-tone={TASK_TONE[state]} title={STATE_HINT[state]}>
          <Icon />
        </span>
        <span className="font-mono text-xs text-muted">{data.taskId ?? data.ref}</span>
        {data.repository?.name ? (
          <span className="flow-pill" title={`Done in ${data.repository.name}: its status there, as the project last heard`}>
            <NodesRight /> {data.repository.name}
          </span>
        ) : null}
        {data.critical ? (
          <span className="flow-pill" data-tone={CRITICAL_TONE} title="On the critical path: a day lost here is a day lost on the plan">
            <Flame /> critical
          </span>
        ) : null}
        <span className="flex-1" />
        <StateLabel state={state} />
      </div>
      <FitText as="div" lines={2} className="plan-card__title">
        {data.title}
      </FitText>
      <div className="mt-auto flex items-center gap-1 text-xs text-muted">
        <Owner name={data.assignee ?? data.role} className="mr-auto" />
        {data.estimate?.complexity ? <span className="flow-pill" title="Estimate, in points">{data.estimate.complexity} pt</span> : null}
        {data.done?.length ? (
          <span className="flow-pill" title={`Delivers ${data.done.join(', ')}`}>
            <Target /> {data.done.length === 1 ? data.done[0] : data.done.length}
          </span>
        ) : null}
        {data.files?.length ? (
          <span className="flow-pill" title={data.files.join('\n')}>
            <FileIcon /> {data.files.length}
          </span>
        ) : null}
        <Verdicts data={data} />
      </div>
      <Handle type="source" position={Position.Right} className={FLOW_HANDLE} />
    </div>
  );
}

const NODE_TYPES = { band: FlowBandNode, planTask: PlanTaskNode };

/** One arrowhead size for every line: a thick line here means the critical path, not a bigger arrow. */
const PLAN_ARROW = 28;
/** As far in as the graph ever opens: past this a small plan is three billboards. */
const PLAN_MAX_ZOOM = 1.15;
/** Room round the graph inside the frame, in pixels. */
const PLAN_FRAME_PAD = { x: 40, y: 24 };
/** The floating task details. */
const PLAN_DETAIL_WIDTH = 288;
/** Never shorter than this: the zoom stack and the overview need somewhere to stand. */
const PLAN_FRAME_MIN = 260;
/** Fit-view options: the graph fills the frame, less the strip along the bottom where the zoom buttons and the overview stand. */
const fitOptions = (footer: number) => ({ padding: { x: `${PLAN_FRAME_PAD.x}px`, top: `${PLAN_FRAME_PAD.y}px`, bottom: `${PLAN_FRAME_PAD.y + footer}px` }, maxZoom: PLAN_MAX_ZOOM }) as const;

function edgeLook(edge: PlanEdgeLayout, moving: boolean): FlowEdgeLook {
  switch (edge.kind) {
    // Both ends done: history. It stays on the picture, and out of the way.
    case 'satisfied':
      return { stroke: FLOW_TONE.green, width: 1.25, opacity: 0.4 };
    case 'blocked':
      return { stroke: FLOW_TONE.rose, width: edge.critical ? 2.5 : 1.75, opacity: 1, dashed: true, zIndex: 1 };
    // Moving only while somebody is working: a finished or unstarted plan is a still picture.
    case 'critical':
      return { stroke: FLOW_TONE[CRITICAL_TONE], width: 2.5, opacity: 1, animated: moving, zIndex: 1 };
    case 'running':
      return { stroke: FLOW_TONE.blue, width: 1.75, opacity: 0.95, animated: true, zIndex: 1 };
    default:
      return { stroke: FLOW_MUTED, width: 1.25, opacity: 0.55 };
  }
}

/** The layout as the canvas's nodes and edges, with whatever is selected lit and the rest stepped back. */
function planFlow(steps: readonly PlanGraphStep[], graph: PlanGraphLayout, chain: Set<string> | null, selectedRef: string | null): { nodes: Node[]; edges: Edge[] } {
  const byRef = new Map(steps.map((n) => [n.ref, n]));
  const nodes: Node[] = graph.waves.map((w) => flowBand(`wave:${w.index}`, w.rect, { title: w.title, description: w.description, tone: w.tone }));
  for (const card of graph.cards) {
    nodes.push({
      id: card.ref,
      type: 'planTask',
      position: { x: card.x, y: card.y },
      style: { width: PLAN_CARD.width, height: PLAN_CARD.height },
      data: { ...byRef.get(card.ref)!, critical: card.critical, state: card.state, selected: card.ref === selectedRef, dimmed: !!chain && !chain.has(card.ref) } satisfies PlanNodeData,
    });
  }
  const edges = graph.edges.map((e) => {
    const look = { ...edgeLook(e, graph.inFlight), ...(e.via ? { via: e.via } : {}), arrow: PLAN_ARROW };
    if (!chain) return flowEdge(e, look);
    const mine = chain.has(e.source) && chain.has(e.target);
    return flowEdge(e, mine ? { ...look, opacity: 1, zIndex: 2 } : { ...look, opacity: 0.07, animated: false, zIndex: 0 });
  });
  return { nodes, edges };
}

/** The counts above the graph, its progress, and what the line colours mean. */
function GraphHeader({ steps, graph }: { steps: readonly PlanGraphStep[]; graph: PlanGraphLayout }) {
  const counts = planCounts(steps);
  const kinds = new Set(graph.edges.map((e) => e.kind));
  const items: FlowCount[] = [
    { key: 'tasks', label: counts.tasks === 1 ? 'task' : 'tasks', n: counts.tasks, tone: 'amber' },
    { key: 'done', label: 'done', n: counts.done, tone: 'green' },
    { key: 'running', label: 'in progress', n: counts.inProgress, tone: 'blue', title: 'Running, or finished and in review' },
    { key: 'blocked', label: 'blocked', n: counts.blocked, tone: counts.blocked ? 'rose' : 'slate', title: 'Blocked, or finished as a failure' },
  ];
  // A plan nobody estimated has no points to count, and "0 pts" would say it was estimated at nothing.
  if (counts.points) items.push({ key: 'points', label: 'pts', n: counts.points, tone: 'slate', title: `${counts.donePoints} of ${counts.points} points done` });
  const legend: FlowCount[] = [
    { key: 'critical', label: 'critical path', tone: CRITICAL_TONE, swatch: 'line', title: 'The longest chain of work: a day lost on it is a day lost on the plan' },
    { key: 'dependency', label: 'dependency', tone: 'slate', swatch: 'line', title: 'The task at the arrow waits for the task at the tail' },
    { key: 'satisfied', label: 'satisfied', tone: 'green', swatch: 'line', title: 'Both ends are done' },
  ];
  if (kinds.has('running')) legend.push({ key: 'live', label: 'into work in progress', tone: 'blue', swatch: 'dashed' });
  if (kinds.has('blocked')) legend.push({ key: 'stuck', label: 'into a blocked task', tone: 'rose', swatch: 'dashed' });
  const total = counts.tasks;
  const done = counts.done;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5" data-plan-graph-head>
      <FlowCounts items={items} data-plan-counts />
      {total > 0 ? (
        <span className="flex min-w-32 flex-1 items-center gap-2" title={`${done} of ${total} tasks done`}>
          <ProgressBar aria-label="Tasks done" size="sm" color={done === total && !counts.blocked ? 'success' : 'accent'} value={done} maxValue={total} className="min-w-0 flex-1">
            <ProgressBar.Track>
              <ProgressBar.Fill />
            </ProgressBar.Track>
          </ProgressBar>
          <span className="shrink-0 text-xs tabular-nums text-muted">{Math.round((done / total) * 100)}%</span>
        </span>
      ) : (
        <span className="flex-1" />
      )}
      <FlowCounts items={legend} data-plan-legend />
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-muted">{title}</span>
      {children}
    </div>
  );
}

/** What a press on a card opens: the whole of the task, what it waits for and what waits for it. It floats over the canvas, so nothing moves. */
function TaskDetail({ steps, graph, selectedRef, criteria, maxHeight, onSelect }: { steps: readonly PlanGraphStep[]; graph: PlanGraphLayout; selectedRef: string; criteria?: PlanGraphProps['criteria']; maxHeight?: number; onSelect: (ref: string | null) => void }) {
  const node = steps.find((n) => n.ref === selectedRef);
  const card = graph.cards.find((c) => c.ref === selectedRef);
  if (!node || !card) return null;
  const Icon = STATE_ICON[card.state];
  const stateOf = new Map(graph.cards.map((c) => [c.ref, c.state]));
  const byRef = new Map(steps.map((n) => [n.ref, n]));
  const waitsFor = node.dependsOn.filter((d) => byRef.has(d));
  const unblocks = steps.filter((n) => n.dependsOn.includes(node.ref)).map((n) => n.ref);
  const delivers = (node.done ?? []).map((id) => ({ id, ac: criteria?.find((a) => a.id === id) }));
  const link = (ref: string) => {
    const n = byRef.get(ref)!;
    const s = stateOf.get(ref) ?? 'waiting';
    return (
      <button key={ref} type="button" className="flex w-full cursor-[var(--cursor-interactive)] items-center gap-1.5 border-0 bg-transparent p-0 text-left text-xs text-foreground hover:text-accent" onClick={() => onSelect(ref)}>
        <span className="flow-dot" data-tone={TASK_TONE[s]} aria-hidden="true" />
        <span className="shrink-0 font-mono text-xs text-muted">{n.taskId ?? n.ref}</span>
        <FitText className="min-w-0 flex-1">{n.title}</FitText>
        <span className="shrink-0 text-xs text-muted">{TASK_LABEL[s]}</span>
      </button>
    );
  };
  return (
    // On the side away from the card, so opening it never covers what was pressed.
    <Panel position={card.x + PLAN_CARD.width / 2 > graph.bounds.x + graph.bounds.width / 2 ? 'top-left' : 'top-right'} className="nowheel nopan nodrag">
      <aside className="flex flex-col gap-2.5 overflow-y-auto rounded-2xl border border-border bg-overlay p-3 shadow-overlay" style={{ width: PLAN_DETAIL_WIDTH, maxHeight }} aria-label={`Task ${node.taskId ?? node.ref}`} data-plan-detail>
        <div className="flex items-center gap-1.5">
          <span className="flow-card__well" data-tone={TASK_TONE[card.state]}>
            <Icon />
          </span>
          <span className="font-mono text-xs text-muted">{node.taskId ?? node.ref}</span>
          <StateLabel state={card.state} />
          <span className="flex-1" />
          <Hint text="Clear the selection (Esc)">
            <Button size="sm" variant="ghost" isIconOnly aria-label="Clear the selection" className="-my-1 -mr-1" onPress={() => onSelect(null)}>
              <Xmark className="size-3.5" />
            </Button>
          </Hint>
        </div>
        <div className="text-sm font-semibold leading-snug text-foreground">{node.title}</div>
        <div className="flex flex-wrap items-center gap-1 text-xs text-muted">
          <Owner name={node.assignee ?? node.role} className="mr-1" />
          {card.critical ? (
            <span className="flow-pill" data-tone={CRITICAL_TONE}>
              <Flame /> critical path
            </span>
          ) : null}
          {node.estimate?.complexity ? <span className="flow-pill">{node.estimate.complexity} pt</span> : null}
          <Verdicts data={node} />
        </div>
        {node.concerns ? <p className="m-0 rounded-lg bg-warning/10 px-2 py-1.5 text-xs leading-snug text-foreground">{node.concerns}</p> : null}
        {delivers.length ? (
          <DetailSection title="Delivers">
            {delivers.map(({ id, ac }) => (
              <div key={id} className="flex items-center gap-1.5 text-xs">
                <span className="flow-dot" data-tone={ac?.passed ? 'green' : 'slate'} aria-hidden="true" />
                <span className="shrink-0 font-mono text-muted">{id}</span>
                <span className="min-w-0 flex-1 truncate text-foreground" title={ac?.title}>
                  {ac?.title ?? ''}
                </span>
                <span className="shrink-0 text-muted">{ac?.passed ? 'met' : 'not checked'}</span>
              </div>
            ))}
          </DetailSection>
        ) : null}
        {waitsFor.length ? <DetailSection title="Waits for">{waitsFor.map(link)}</DetailSection> : null}
        {unblocks.length ? <DetailSection title="Unblocks">{unblocks.map(link)}</DetailSection> : null}
        {node.files?.length ? (
          <DetailSection title={`Files · ${node.files.length}`}>
            {node.files.slice(0, 5).map((f) => (
              <FitText as="div" key={f} className="font-mono text-xs text-muted">
                {f}
              </FitText>
            ))}
            {node.files.length > 5 ? (
              <div className="text-xs text-muted" title={node.files.slice(5).join('\n')}>
                and {node.files.length - 5} more
              </div>
            ) : null}
          </DetailSection>
        ) : null}
      </aside>
    </Panel>
  );
}

/**
 * The plan as a picture: waves of work, left to right, under their counts. The room there is gets measured so the
 * layout can fill it — a long chain wraps into rows rather than shrinking to a strip — and the frame is then made
 * no taller than the graph needs.
 */
export function PlanGraph({ planId, steps, critical, criteria }: PlanGraphProps) {
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedRef) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      setSelectedRef(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [selectedRef]);

  // Rounded, so a window dragged a pixel at a time does not lay the graph out again each pixel.
  const room = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState<{ width: number; height: number } | undefined>();
  useEffect(() => {
    const el = room.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const read = () => {
      const next = { width: Math.round(el.clientWidth / 8) * 8, height: Math.round(el.clientHeight / 8) * 8 };
      setFrame((cur) => (cur && cur.width === next.width && cur.height === next.height ? cur : next));
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The strip along the bottom for the zoom buttons and the overview is not room for the graph.
  // A phone-wide frame has no room for the overview beside the graph: the zoom buttons are enough there.
  const roomy = (frame?.width ?? 0) >= 640;
  const minimap = useMemo(() => roomy && planWantsMinimap(steps.length, new Set(planWaves(steps).values()).size), [steps, roomy]);
  const chrome = PLAN_FRAME_PAD.y * 2 + flowFooter(minimap);
  const fit = useMemo(() => fitOptions(flowFooter(minimap)), [minimap]);
  const innerWidth = frame ? Math.max(1, frame.width - PLAN_FRAME_PAD.x * 2) : undefined;
  const innerHeight = frame ? Math.max(1, Math.max(frame.height, PLAN_FRAME_MIN) - chrome) : undefined;
  const graph = useMemo(() => layoutPlanGraph(steps, critical, { ...(innerWidth && innerHeight ? { frame: { width: innerWidth, height: innerHeight } } : {}), maxZoom: PLAN_MAX_ZOOM }), [steps, critical, innerWidth, innerHeight]);
  const flow = useMemo(() => {
    const chain = selectedRef ? planChain(steps, selectedRef) : null;
    const { nodes, edges } = planFlow(steps, graph, chain?.size ? chain : null, selectedRef);
    return { nodes: withNodeSizes(nodes, PLAN_NODE_SIZES), edges };
  }, [steps, graph, selectedRef]);

  // As tall as the graph at the zoom it will get, and no taller.
  const zoom = innerWidth && innerHeight && graph.bounds.width ? Math.min(PLAN_MAX_ZOOM, innerWidth / graph.bounds.width, innerHeight / graph.bounds.height) : 1;
  const height = frame ? Math.min(Math.max(frame.height, PLAN_FRAME_MIN), Math.max(PLAN_FRAME_MIN, Math.ceil(graph.bounds.height * zoom + chrome))) : undefined;

  // What identifies the picture, for FlowRefit: not the statuses, which tick.
  const shape = `${planId}:${graph.cards.length}:${graph.rows}`;

  const stateOf = useMemo(() => new Map(graph.cards.map((c) => [c.ref, c.state])), [graph]);
  const overviewColor = useCallback((node: Node) => (stateOf.has(node.id) ? FLOW_TONE[TASK_TONE[stateOf.get(node.id)!]] : undefined), [stateOf]);
  return (
    <div className="flex min-h-[22rem] flex-1 flex-col gap-3" data-plan-graph-panel>
      <GraphHeader steps={steps} graph={graph} />
      <div ref={room} className="min-h-0 flex-1" data-plan-graph data-rows={graph.rows}>
        <FlowCanvas
          frameStyle={{ height: height ?? '100%' }}
          nodes={flow.nodes}
          edges={flow.edges}
          nodeTypes={NODE_TYPES}
          fitView
          fitViewOptions={fit}
          minZoom={0.2}
          maxZoom={2}
          nodesDraggable={false}
          minimap={minimap}
          footer
          minimapNodeColor={overviewColor}
          onNodeClick={(_, node) => {
            if (node.type === 'planTask') setSelectedRef((cur) => (cur === node.id ? null : node.id));
          }}
          onPaneClick={() => setSelectedRef(null)}
        >
          <FlowRefit options={fit} shape={shape} />
          {selectedRef ? <TaskDetail steps={steps} graph={graph} selectedRef={selectedRef} {...(criteria ? { criteria } : {})} {...(height ? { maxHeight: height - flowFooter(minimap) - 30 } : {})} onSelect={setSelectedRef} /> : null}
        </FlowCanvas>
      </div>
    </div>
  );
}

/** A plan step's status as the graph reads it: the home's step words (todo, doing, done, blocked) or a board status. */
export function stepStatus(status: unknown): string | undefined {
  if (typeof status !== 'string') return undefined;
  return status === 'doing' ? 'in-progress' : status;
}

/** The longest chain of steps, in order: the critical path when the plan does not say its own. */
export function longestChain(steps: readonly PlanGraphTask[]): string[] {
  const waves = planWaves(steps);
  const byRef = new Map(steps.map((s) => [s.ref, s]));
  let end: string | undefined;
  for (const [ref, w] of waves) if (end === undefined || w > waves.get(end)!) end = ref;
  const chain: string[] = [];
  while (end) {
    chain.unshift(end);
    const before: string[] = (byRef.get(end)?.dependsOn ?? []).filter((d) => waves.has(d));
    end = before.sort((a, b) => waves.get(b)! - waves.get(a)!)[0];
  }
  return chain.length > 1 ? chain : [];
}
